import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  MapPinned,
  Users,
  X,
  Network,
  Waypoints,
  BarChart3,
  Activity,
  CalendarDays,
} from "lucide-react";

import { useAuth } from "@/auth/auth-provider";
import { hasCapability, type Capability } from "@/auth/capabilities";
import { reportingAdmin } from "@/lib/reporting";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import esumeiLogoUrl from "@/assets/brand/esumei-logo.svg";
import anietronicLogoUrl from "@/assets/brand/anietronic-logo-grey.jpeg";

const NAV_ITEMS = [
  {
    to: "/reports/performance",
    label: "Performance",
    icon: BarChart3,
    capability: "analytics:read",
  },
  {
    to: "/reports/reliability",
    label: "Reliability",
    icon: Activity,
    capability: "analytics:read",
  },
  {
    to: "/reports/daily-dar",
    label: "Daily DAR",
    icon: CalendarDays,
    capability: "analytics:read",
  },
] as const;

const ADMIN_ITEMS = [
  {
    to: "/controllers",
    label: "Controllers",
    icon: Network,
    capability: "controllers:read",
  },
  {
    to: "/feeders",
    label: "Feeders",
    icon: Waypoints,
    capability: "fleet:read",
  },
  { to: "/organizations", label: "Organizations", icon: Building2, capability: "catalog:write" },
  { to: "/substations", label: "Substations", icon: MapPinned, capability: "catalog:write" },
  { to: "/users", label: "Users", icon: Users, capability: "users:manage" },
] as const;

export function Sidebar({
  mobileOpen,
  onMobileClose,
}: {
  mobileOpen: boolean;
  onMobileClose: () => void;
}): JSX.Element {
  const { me } = useAuth();
  const location = useLocation();
  const reportSearch: string =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/reports") ||
    location.pathname === "/compliance"
      ? location.search
      : "";

  const allowed = (capability: Capability) => hasCapability(me, capability);
  const items = NAV_ITEMS.filter((item) => allowed(item.capability));
  const adminItems = ADMIN_ITEMS.filter((item) => allowed(item.capability));

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={onMobileClose}
        />
      ) : null}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200 lg:static lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-sidebar-border px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-20 shrink-0 items-center justify-center rounded-lg bg-[#111827] px-2 shadow-sm">
              <img src={esumeiLogoUrl} alt="ESUMEI" className="max-h-8 w-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold leading-none">ESUMEI Cloud</span>
              <span className="text-xs text-sidebar-foreground/60">Operations Console</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground lg:hidden"
            onClick={onMobileClose}
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {allowed("analytics:read") ? <NavLink
            to={{ pathname: "/dashboard", search: reportSearch }}
            onClick={onMobileClose}
            className={({ isActive }) =>
              cn(
                "mb-5 flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
              )
            }
          >
            <LayoutDashboard className="h-5 w-5" />
            <span>Executive Summary</span>
          </NavLink> : null}
          <div role="group" aria-label="Analytics" className="space-y-1">
            <div className="px-3 pb-2 pt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/45">
              Analytics
            </div>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={{ pathname: item.to, search: reportSearch }}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    cn(
                      "ml-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          {allowed("analytics:read") ? (
            <div role="group" aria-label="Reporting" className="space-y-1">
              <div className="px-3 pb-2 pt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/45">
                Reporting
              </div>
              {[
                { to: "/reporting/exports", label: "Report exports" },
                ...(reportingAdmin(me)
                  ? [{ to: "/reporting/schedules", label: "Scheduled deliveries" }]
                  : []),
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    cn(
                      "ml-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                    )
                  }
                >
                  <CalendarDays className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ) : null}
          {adminItems.length > 0 ? (
            <div role="group" aria-label="Administration" className="space-y-1">
              <div className="px-3 pt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/45">
                Administration
              </div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ) : null}
        </nav>

        <div className="border-t border-sidebar-border px-5 py-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sidebar-foreground/45">
            powered by
          </div>
          <div className="mt-2 inline-flex max-w-[160px] rounded-md bg-white px-2 py-1 shadow-sm">
            <img
              src={anietronicLogoUrl}
              alt="Anietronic"
              className="h-4 w-auto max-w-[140px] object-contain"
            />
          </div>
        </div>
      </aside>
    </>
  );
}
